import { SelectItem } from '@panwds/react-ui';
import { Select, Input, FormLayout } from '@panwds/react-form';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslate } from "../../../customHooks";
import { SubscriptionAccountIdField, SubscriptionBillingCodeField } from './FormFields';
import { SubscriptionSubmitButton } from './ButtonComponents';
import {
    SUBSCRIPTION_FIELDS,
    SUBSCRIPTION_I18N_KEYS,
    SUBSCRIPTION_METRICS,
    SUBSCRIPTION_TOAST_IDS
} from '../constants';
import { toast } from '../../../components';
import { useGetCloudmanagerQuery } from '../../../redux/services/cloudmanager-service';
import { useMemo, useEffect } from 'react';
import * as queryString from 'query-string';

interface AirfmLinkAccountFormProps {
    formMethods: UseFormReturn<any>;
    accountId: string;
    email?: string;
    tenantChoices: SelectItem[] | undefined;
    subscriptionsLoading: boolean;
    billingAssociationCode?: string;
    isValid: boolean;
    hasError?: boolean;
    onSubmit: (params: any) => void;
    authToken?: string;
}

/**
 * AIRFM specific link account form
 * TODO: Define AIRFM-specific form fields based on product requirements
 *
 * This is a placeholder implementation. Update with actual AIRFM fields when requirements are defined.
 * Potential AIRFM-specific fields might include:
 * - AIRFM deployment type
 * - AIRFM region selector
 * - AIRFM-specific configuration options
 */
export const AirfmLinkAccountForm = ({
    formMethods,
    accountId,
    email,
    tenantChoices,
    subscriptionsLoading,
    billingAssociationCode,
    isValid,
    hasError,
    onSubmit,
    authToken
}: AirfmLinkAccountFormProps) => {
    const translate = useTranslate();

    // Use first tenant if only one is available, otherwise let user select
    const tenant = tenantChoices?.length === 1 ? tenantChoices[0].value : undefined;

    // Parse tsg-ids from query parameters (can appear multiple times: &tsg-ids=val1&tsg-ids=val2)
    const allowedTsgIds = useMemo(() => {
        const parsedParams = queryString.parse(window?.location?.search);
        const tsgIdsParam = parsedParams['tsg-ids'];

        if (!tsgIdsParam) return null;

        // Handle both string (single value) and array (multiple values)
        return Array.isArray(tsgIdsParam) ? tsgIdsParam : [tsgIdsParam];
    }, []);

    // Prepare headers for Cloud Manager API call
    const headers = authToken && tenant ? {
        Authorization: authToken,
        'x-api-key': String(tenant),
        'Content-Type': 'application/json',
    } : undefined;

    const { data: cms, error: getCmsError, isLoading: getCmsLoading } = useGetCloudmanagerQuery(
        { headers },
        { skip: !authToken || !tenant } // Skip query if no auth credentials
    );

    // Extract CspIds from cloud manager data's IntegrationsAll
    const cspIdOptions = useMemo<SelectItem[]>(() => {
        if (!cms || !cms.IntegrationsAll || !Array.isArray(cms.IntegrationsAll)) return [];

        // Extract unique CspIds from IntegrationsAll
        const uniqueCspIds = [...new Set(cms.IntegrationsAll.map((integration: any) => integration.CspId))];

        return uniqueCspIds
            .filter(Boolean) // Remove null/undefined values
            .map((cspId: any) => ({
                value: cspId,
                children: cspId
            }));
    }, [cms]);

    // Watch the selected CspId to filter TsgIds
    const selectedCspId = formMethods.watch('CspId');
    const selectedTsgId = formMethods.watch('TsgId');

    // Extract TsgIds for the selected CspId from IntegrationsAll
    const tsgIdOptions = useMemo<SelectItem[]>(() => {
        if (!cms || !cms.IntegrationsAll || !Array.isArray(cms.IntegrationsAll) || !selectedCspId) return [];

        // Find the integration object that matches the selected CspId
        const matchingIntegration = cms.IntegrationsAll.find((integration: any) => integration.CspId === selectedCspId);

        if (!matchingIntegration || !matchingIntegration.Integrations || !Array.isArray(matchingIntegration.Integrations)) {
            return [];
        }

        // Store TsgId as value for display, but keep full object in context for later use
        return matchingIntegration.Integrations.map((item: any) => {
            const isLinked = allowedTsgIds ? allowedTsgIds.includes(item.TsgId) : false;
            return {
                value: item.TsgId,
                children: isLinked ? `${item.TsgId} (Linked)` : item.TsgId,
                context: item, // Store the entire integration object in context
                disabled: isLinked
            };
        });
    }, [cms, selectedCspId, allowedTsgIds]);

    // When TsgId is selected, store the full integration object in a hidden field
    useEffect(() => {
        if (selectedTsgId && tsgIdOptions.length > 0) {
            const selectedOption = tsgIdOptions.find(option => option.value === selectedTsgId);
            if (selectedOption?.context) {
                formMethods.setValue('TsgIdObject', selectedOption.context);
            }
        }
    }, [selectedTsgId, tsgIdOptions, formMethods]);

    useEffect(() => {
        if (getCmsError) {
            toast.error((getCmsError as any)?.error || translate(SUBSCRIPTION_I18N_KEYS.REQUEST_FAILED), {
                toastId: SUBSCRIPTION_TOAST_IDS.CLOUD_MANAGER
            });
        }
    }, [getCmsError]);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                <FormLayout>
                        <SubscriptionAccountIdField
                            value={accountId}
                            dataMetrics={SUBSCRIPTION_METRICS.LINK_ACCOUNT_ACCOUNT_ID}
                        />

                        {email && hasError && (
                            <Input
                                name={SUBSCRIPTION_FIELDS.USER_NAME}
                                type="email"
                                label={translate(SUBSCRIPTION_I18N_KEYS.EMAIL)}
                                dataMetrics={SUBSCRIPTION_METRICS.LINK_ACCOUNT_EMAIL}
                                value={email}
                                readOnly
                                placeholder=""
                            />
                        )}

                        {(tenantChoices?.length && tenantChoices?.length > 1) && (
                            <Select
                                items={tenantChoices}
                                label={translate(SUBSCRIPTION_I18N_KEYS.TENANT_ID)}
                                name={SUBSCRIPTION_FIELDS.TENANT_ID}
                                dataMetrics={SUBSCRIPTION_METRICS.LINK_ACCOUNT_TENANT}
                                dataTestId={SUBSCRIPTION_METRICS.LINK_ACCOUNT_TENANT}
                                requiredAsterisk
                                simpleFormat
                                isLoading={subscriptionsLoading}
                                placeholder=""
                            />
                        )}

                        {billingAssociationCode && (
                            <SubscriptionBillingCodeField
                                value={billingAssociationCode}
                                dataMetrics={SUBSCRIPTION_METRICS.LINK_ACCOUNT_ASSOCIATION_CODE}
                                readOnly={!hasError}
                            />
                        )}

                        <Select
                            label="CSP ID"
                            name="CspId"
                            items={cspIdOptions}
                            requiredAsterisk
                            dataMetrics="airfm-csp-id-select"
                            dataTestId="airfm-csp-id-select"
                            simpleFormat
                            isLoading={getCmsLoading}
                            placeholder=""
                        />

                        {/* TSG ID Selection - only show when a CSP ID is selected */}
                        {selectedCspId && (
                            <Select
                                label="TSG ID"
                                name="TsgId"
                                items={tsgIdOptions}
                                requiredAsterisk
                                dataMetrics="airfm-tsg-id-select"
                                dataTestId="airfm-tsg-id-select"
                                simpleFormat
                                placeholder=""
                            />
                        )}

                        <SubscriptionSubmitButton
                            dataMetrics={SUBSCRIPTION_METRICS.LINK_ACCOUNT_CONTINUE}
                            disabled={!isValid}
                        >
                            {translate(SUBSCRIPTION_I18N_KEYS.CONTINUE)}
                        </SubscriptionSubmitButton>
                </FormLayout>
            </form>
        </FormProvider>
    );
};
